import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectIsOnline, selectSceneDbBackfill } from "slices/explorer";

export interface DbStatus {
    /** Id-based reads (`getObject`, properties) will answer. */
    isDataAvailable: boolean;
    /** Hierarchy and search reads (children, search, selection by query) will answer. */
    isIndexAvailable: boolean;
    /** Both of the above — what browsing the hierarchy needs. */
    isAvailable: boolean;
    /**
     * Why the server cannot answer, whether or not an offline index covers for it —
     * check the `is*Available` flags first. Offline wins when both apply.
     */
    unavailableReason: "offline" | "preparing" | undefined;
}

/**
 * Whether the scene's object data can be read right now, and why not.
 *
 * A downloaded index answers regardless of the connection or a server-side backfill,
 * so the offline capabilities of `dataAccess` are what the flags fall back to.
 */
export function useDbStatus(): DbStatus {
    const isOnline = useAppSelector(selectIsOnline);
    const backfill = useAppSelector(selectSceneDbBackfill);
    const {
        state: { dataAccess },
    } = useExplorerGlobals();

    // A backfill that finished mid-session still needs a reload: this session's one-shot init already failed.
    const unavailableReason = !isOnline ? "offline" : backfill.preparing || backfill.ready ? "preparing" : undefined;
    const isServerAvailable = unavailableReason === undefined;
    const isDataAvailable = isServerAvailable || Boolean(dataAccess?.isDataAvailableOffline());
    const isIndexAvailable = isServerAvailable || Boolean(dataAccess?.isIndexAvailableOffline());

    return {
        isDataAvailable,
        isIndexAvailable,
        isAvailable: isDataAvailable && isIndexAvailable,
        unavailableReason,
    };
}
